import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { UnitBalanceService } from 'src/app/core/services/unit-balance.service';
import { AllocationRequest, AllocationResult, BalanceTransaction, EntityBalance, TenantBalances } from './account-balance.models';
import { transactionHistoryField } from './product-catalog';

/**
 * Reads and writes unit balances for the account overview page.
 *
 * Keeps the cloud functions' string protocol (`response == 'SUCCESS'`,
 * `message == 'Insufficient Credits'`) out of the component. Errors are left to
 * propagate: the caller owns the reporting context.
 */
@Injectable({ providedIn: 'root' })
export class AccountOverviewService {
  private readonly SUCCESS = 'SUCCESS';
  private readonly INSUFFICIENT_CREDITS = 'Insufficient Credits';

  constructor(private unitBalanceService: UnitBalanceService) { }

  /**
   * Tenant-wide balances plus one entry per company.
   *
   * `companyKey` scopes the cloud function's response to that company, so a page
   * showing one company at a time does not pull every company on the tenant. Omit it
   * to get them all.
   *
   * The result is filtered to `companyKeys` either way: that is the permission
   * boundary, and it must not depend on the backend honouring the scope.
   *
   * Returns null when the cloud function reports anything but success.
   */
  fetchBalances(tenantKey: string, companyKeys: string[], companyKey?: string): Observable<TenantBalances> {
    const request = companyKey ? { tenantKey, companyKey } : { tenantKey };

    return this.unitBalanceService.fetchTenantCompanyBalance(request).pipe(
      map((response: any) => {
        if (response?.response != this.SUCCESS)
          return null;

        const balances: TenantBalances = response.data;
        balances.entities = (balances.entities || []).filter(entity => companyKeys.includes(entity.key));
        return balances;
      })
    );
  }

  allocateUnits(request: AllocationRequest): Observable<AllocationResult> {
    return this.unitBalanceService.distributeUnitsToCompany(request).pipe(
      map((response: any) => ({
        success: response?.response == this.SUCCESS,
        insufficientCredits: response?.message == this.INSUFFICIENT_CREDITS,
        mainBalance: response?.mainBalance
      }))
    );
  }

  /** Tenant-wide purchase history for a product. */
  tenantHistory(balances: TenantBalances, productKey: string): BalanceTransaction[] {
    return (balances?.[transactionHistoryField(productKey)] as BalanceTransaction[])?.sort((a, b) => b.createdDate - a.createdDate) || [];
  }

  /** A company's allocation history for a product. */
  entityHistory(entity: EntityBalance, productKey: string): BalanceTransaction[] {
    return (entity?.balanceDistribution?.[transactionHistoryField(productKey)] as BalanceTransaction[])?.sort((a, b) => b.createdDate - a.createdDate) || [];
  }
}
